import { useMemo } from "react";
import { ErrorState } from "@/components/ui/States";
import { useItem, useList } from "@/lib/api/queries";
import { qty } from "@/lib/format";

/**
 * Buyurtma satrlari jadvali: tovar, birlik va miqdorlar.
 *
 * Bir xil jadval ikki joyda: «SKU lar» paneli va «Terishga chiqarish»
 * oynasi. Ilgari chiqarish oynasida satrlar
 * yo'q edi — dispetcher NIMANI chiqarayotganini ko'rmay qaror
 * qilardi.
 */

export interface PickingLine {
  id: string;
  line_number: number;
  sku_id: string;
  requested_qty: string;
  allocated_qty?: string;
  picked_qty?: string;
  unallocated_qty?: string;
  status?: string;
}

interface Sku {
  id: string;
  article: string;
  name: string;
  base_uom?: string;
  /** Qadoq shakli («2x6») — tovar nomi oxiridan. */
  pack_format?: string;
}

interface Availability {
  available_qty: string;
  pickable?: boolean;
}

export function useOrderDetail<T extends { lines?: PickingLine[] }>(orderId: string) {
  return useItem<T>("picking", `/api/v1/picking/orders/${orderId}`);
}

export function OrderLinesTable({
  orderId,
  warehouseCode,
  showAvailable = false,
}: {
  orderId: string;
  warehouseCode?: string;
  /** Terishga chiqarishdan oldin: omborda terish mumkin bo'lgan qoldiq. */
  showAvailable?: boolean;
}) {
  const detail = useOrderDetail<{ lines?: PickingLine[] }>(orderId);
  const lines = useMemo(() => detail.data?.lines ?? [], [detail.data]);
  const ids = useMemo(() => [...new Set(lines.map((line) => line.sku_id))].join(","), [lines]);
  const skus = useList<Sku>("masterData", "/api/v1/skus", { ids, limit: 500 }, { enabled: ids !== "" });
  const names = new Map((skus.data?.items ?? []).map((sku) => [sku.id, sku]));

  if (detail.isLoading) return <div className="card skeleton" style={{ height: 120 }} />;
  if (detail.error) return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  if (lines.length === 0) return <div className="cell-sub">Buyurtmada satr yo'q.</div>;

  const total = (pick: (line: PickingLine) => string | undefined) =>
    lines.reduce((sum, line) => sum + (Number(pick(line)) || 0), 0);

  return (
    <table className="data">
      <thead>
        <tr>
          <th>#</th>
          <th>Tovar</th>
          <th>Birlik</th>
          <th className="right">Reja</th>
          {showAvailable && <th className="right">Mavjud</th>}
          <th className="right">Rezerv</th>
          <th className="right">Terildi</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => {
          const sku = names.get(line.sku_id);
          const planned = line.requested_qty;
          return (
            <tr key={line.id}>
              <td className="num">{line.line_number}</td>
              <td>
                {sku ? (
                  <>
                    <span className="mono">{sku.article}</span> · {sku.name}
                  </>
                ) : (
                  <span className="mono">{line.sku_id.slice(0, 8)}</span>
                )}
              </td>
              <td>
                {sku?.base_uom ?? "—"}
                {sku?.pack_format && <span className="mono"> · {sku.pack_format}</span>}
              </td>
              <td className="num right">{qty(planned)}</td>
              {showAvailable && (
                <td className="num right">
                  <AvailableQty skuId={line.sku_id} warehouseCode={warehouseCode} planned={planned} />
                </td>
              )}
              <td className="num right">
                {qty(line.allocated_qty)}
              </td>
              <td className="num right">
                {qty(line.picked_qty)}
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <td />
          <td>
            <strong>Jami: {lines.length} SKU</strong>
          </td>
          <td />
          <td className="num right">
            <strong>{total((line) => line.requested_qty)}</strong>
          </td>
          {showAvailable && <td />}
          <td className="num right">
            {total((line) => line.allocated_qty)}
          </td>
          <td className="num right">
            {total((line) => line.picked_qty)}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

/**
 * Omborda TERISH MUMKIN bo'lgan qoldiq (bloklangan katak va
 * rezervsiz). Rejadan kam bo'lsa qizil — chiqarishda shu qism ochiq
 * qoladi.
 */
function AvailableQty({
  skuId,
  warehouseCode,
  planned,
}: {
  skuId: string;
  warehouseCode?: string;
  planned: string;
}) {
  const stock = useList<Availability>("inventory", "/api/v1/stock/availability", {
    sku_id: skuId,
    warehouse_code: warehouseCode,
    only_pickable: true,
    limit: 500,
  });
  if (stock.isLoading) return <span className="cell-sub">…</span>;
  if (stock.error) return <>—</>;
  const sum = (stock.data?.items ?? []).reduce((total, row) => total + (Number(row.available_qty) || 0), 0);
  const short = sum < (Number(planned) || 0);
  return (
    <span style={short ? { color: "var(--error)", fontWeight: 600 } : undefined} title={short ? "Rejadan kam" : undefined}>
      {sum.toLocaleString("uz", { maximumFractionDigits: 3 })}
    </span>
  );
}
